import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, LinkIcon, RefreshCcw, Unplug, Stethoscope, Activity, AlertTriangle, Users, XCircle, Search, Wrench } from "lucide-react";

const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString() : "—"; } catch { return iso || "—"; } };

export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncPhase, setSyncPhase] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [clientIdPrefix, setClientIdPrefix] = useState("");
  const [callbackError, setCallbackError] = useState(null);
  const [probeResult, setProbeResult] = useState(null);
  const [probing, setProbing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [relinking, setRelinking] = useState(false);
  const [sp] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, ] = await Promise.all([api.get("/drchrono/status")]);
      setStatus(data);
    } catch { toast.error("Could not load status"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const dr = sp.get("drchrono");
    const reason = sp.get("reason");
    if (dr === "connected") toast.success("DrChrono connected!");
    if (dr === "error") {
      toast.error(`DrChrono failed: ${reason || "unknown"}`, { duration: 10000 });
      setCallbackError({ reason });
      api.get("/drchrono/last-callback-error").then(r => setCallbackError(prev => ({ ...(prev || {}), ...r.data }))).catch(() => {});
    }
    api.get("/drchrono/current-client-id").then(r => setClientIdPrefix(r.data?.client_id_first10 || "")).catch(() => {});
    // Also load any in-progress backfill so we can display it and resume polling
    api.get("/drchrono/backfill-cpt-status").then(r => {
      setBackfillJob(r.data);
      if (r.data && ["done", "error", "never_run"].indexOf(r.data.phase) === -1) {
        pollBackfill();
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/drchrono/authorize");
      window.location.href = data.url;
    } catch { toast.error("Could not start DrChrono authorization"); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect DrChrono?")) return;
    try { await api.post("/drchrono/disconnect"); toast.success("Disconnected"); load(); }
    catch { toast.error("Could not disconnect"); }
  };

  const hardReset = async () => {
    if (!window.confirm("Hard reset revokes the token at DrChrono AND clears our local token. Proceed?")) return;
    try {
      await api.post("/drchrono/hard-reset");
      toast.success("Hard reset done. Go to drchrono.com/o/authorized_tokens/ to also revoke the app grant, then reconnect.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hard reset failed"); }
  };

  const syncNow = async (force = false) => {
    setSyncing(true); setSyncPhase("Queuing…");
    try {
      const { data } = await api.post("/drchrono/sync", null, { params: force ? { force: true } : {} });
      const jobId = data.job_id;
      if (!jobId) { load(); return; }
      const phaseLabels = {
        queued: "Starting…", offices: "Fetching offices…",
        doctors: "Fetching doctors…",
        appointments_whole_practice: "Pass A: whole practice…",
        appointments_per_doctor: "Pass B: per-doctor…",
        appointments_per_office: "Pass C: per-office…",
        line_item_recovery: "Pass D: line-item recovery (can take minutes)…",
        upserting_visits: "Upserting visits…",
        backfill_visits: "Backfilling old visits (date + NP link)…",
        patients: "Fetching patients…",
        medications: "Fetching medications…",
        allergies: "Fetching allergies…",
        problems: "Fetching problems…",
        clinical_notes: "Fetching clinical notes…",
        done: "Done!", error: "Error",
      };
      let last = "";
      const started = Date.now();
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: st } = await api.get(`/drchrono/sync-status/${jobId}`);
        const p = st.phase || "queued";
        if (p !== last) { last = p; setSyncPhase(phaseLabels[p] || p); }
        if (p === "done") {
          const r = st.result || {};
          toast.success(`Sync done · ${r.facilities_updated || 0} fac · ${r.nps_upserted || 0} NPs · ${r.mds_upserted || 0} MDs · ${r.visits_upserted || 0} visits · ${r.patients_upserted || 0} pts`, { duration: 20000 });
          load(); return;
        }
        if (p === "error") { toast.error(`Sync failed: ${st.error || "unknown"}`, { duration: 15000 }); return; }
        if (Date.now() - started > 35 * 60 * 1000) { toast.info("Sync still running — the backend has a 30-min budget and may still be finalizing. Refresh in a few minutes to see updated live totals above.", { duration: 15000 }); return; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); setSyncPhase(""); }
  };

  const cancelStuck = async () => {
    if (!window.confirm("Cancel any stuck DrChrono sync jobs? This marks hung jobs as 'cancelled' so a fresh sync can run. Safe to click if a job has been running for over 5 minutes with no progress.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/drchrono/cancel-stuck-sync");
      if (data.cancelled_count > 0) {
        toast.success(`Cancelled ${data.cancelled_count} stuck job(s). You can now Force Sync.`, { duration: 8000 });
      } else {
        toast.info("No stuck jobs found — sync engine is idle.");
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setCancelling(false); }
  };

  const runDiagnose = async () => {
    setDiagnosing(true); setDiagResult(null);
    try {
      const { data } = await api.get("/drchrono/diagnose-visits");
      setDiagResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Diagnose failed"); }
    finally { setDiagnosing(false); }
  };

  const relinkOrphans = async () => {
    if (!window.confirm("Re-link every orphan DrChrono visit to its NP based on drchrono_doctor_id mappings? This is safe and idempotent.")) return;
    setRelinking(true);
    try {
      const { data } = await api.post("/drchrono/relink-orphan-visits");
      toast.success(`Linked ${data.linked} orphan visits. ${data.skipped_no_np_for_doctor} still orphan (unmapped doctors).`, { duration: 12000 });
      await runDiagnose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Relink failed"); }
    finally { setRelinking(false); }
  };

  const [historyResult, setHistoryResult] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const loadHistory = async () => {
    setLoadingHistory(true); setHistoryResult(null);
    try {
      const { data } = await api.get("/drchrono/full-history");
      setHistoryResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "History load failed"); }
    finally { setLoadingHistory(false); }
  };

  const [reconcileResult, setReconcileResult] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const runReconcile = async () => {
    setReconciling(true); setReconcileResult(null);
    try {
      const { data } = await api.get("/drchrono/reconcile");
      setReconcileResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reconcile failed"); }
    finally { setReconciling(false); }
  };

  const [backfillingCpt, setBackfillingCpt] = useState(false);
  const [backfillJob, setBackfillJob] = useState(null);
  const pollBackfill = async () => {
    try {
      const { data } = await api.get("/drchrono/backfill-cpt-status");
      setBackfillJob(data);
      if (data && ["done", "error", "never_run"].indexOf(data.phase) === -1) {
        setTimeout(pollBackfill, 10000);  // poll every 10s
      } else if (data?.phase === "done") {
        toast.success(`Backfill complete · ${data.visits_updated_with_codes} visits got real CPT codes · ${data.visits_cleared_empty_codes} cleared. Refresh Overview to see updated stats.`, { duration: 20000 });
        load();
      } else if (data?.phase === "error") {
        toast.error(`Backfill errored: ${data.error || "unknown"}. Wait 60 min and retry.`, { duration: 15000 });
      }
    } catch { /* silent */ }
  };
  const backfillCpt = async () => {
    if (!window.confirm("Refetch DrChrono line_items and rewrite CPT codes on every visit? Runs in background with auto-retry on rate-limits (up to 3 attempts, ~5-10 min total). Safe.")) return;
    setBackfillingCpt(true);
    try {
      const { data } = await api.post("/drchrono/backfill-cpt-codes");
      toast.info(data.message || "Backfill queued", { duration: 12000 });
      pollBackfill();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill dispatch failed"); }
    finally { setBackfillingCpt(false); }
  };

  const runProbe = async () => {
    setProbing(true); setProbeResult(null);
    try {
      const { data } = await api.get("/drchrono/probe");
      setProbeResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Probe failed"); }
    finally { setProbing(false); }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-6" data-testid="settings-page">
      <h1 className="heading text-3xl font-semibold text-[#292723] mb-6">Settings</h1>

      <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[#F0EBE1] grid place-items-center">
            <Stethoscope className="h-5 w-5 text-[#4A6B5D]"/>
          </div>
          <div className="flex-1">
            <h2 className="heading text-xl font-semibold text-[#292723]">DrChrono EHR</h2>
            <p className="text-sm text-[#5C5850] mt-0.5">Nightly sync of offices, doctors, appointments, line items, patients + clinical data.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-[#8A847A]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/>Loading…</div>
        ) : status?.connected ? (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-sm text-[#3B5C46]">
              <CheckCircle2 className="h-4 w-4"/> <b>Connected</b>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-[#5C5850]">
              <div><span className="text-[#8A847A]">Connected on</span><br/>{fmt(status.token?.connected_at)}</div>
              <div><span className="text-[#8A847A]">Token expires</span><br/>{fmt(status.token?.expires_at)}</div>
            </div>
            {status.current_job && status.current_job.phase !== "done" && status.current_job.phase !== "queued" && (
              <div className="mt-3 p-3 rounded bg-[#F0EBE1] border border-[#E5D9BE] text-xs">
                <div className="font-semibold text-[#8A6E2A]">Latest sync job: {status.current_job.phase}</div>
                {status.current_job.error && (
                  <div className="mt-1 text-[#8A3E30] font-mono text-[10px]">{status.current_job.error}</div>
                )}
                {status.current_job.finished_at && (
                  <div className="mt-1 text-[#8A6E2A] text-[10px]">Finished {fmt(status.current_job.finished_at)}</div>
                )}
              </div>
            )}
            {status.live_totals && (
              <div className="border-t border-[#E5E0D8] pt-3 text-xs" data-testid="live-totals-card">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#3B5C46] font-semibold">✓ Live data in your database</div>
                  {status.live_totals.newest_visit_updated_at && (
                    <div className="text-[10px] text-[#8A847A]">Last write: {fmt(status.live_totals.newest_visit_updated_at)}</div>
                  )}
                </div>
                <div className="mt-2 text-[#292723]">
                  {status.live_totals.facilities || 0} fac · <b>{status.live_totals.nps_mapped || 0} of {status.live_totals.nps_total || 0} NPs mapped</b> · <b>{(status.live_totals.visits || 0).toLocaleString()} visits</b> · {(status.live_totals.patients || 0).toLocaleString()} patients
                </div>
                <div className="mt-1 text-[#5C5850]">
                  <b>{(status.live_totals.visits_with_cpt || 0).toLocaleString()}</b> visits w/ CPT · {(status.live_totals.medications || 0).toLocaleString()} meds · {(status.live_totals.allergies || 0).toLocaleString()} allergies · {(status.live_totals.problems || 0).toLocaleString()} problems · <b>{(status.live_totals.clinical_notes || 0).toLocaleString()} notes</b>
                </div>
              </div>
            )}
            {status.last_sync && (
              <div className="mt-2 text-[10px] text-[#8A847A]" data-testid="last-sync-record">
                Last sync <i>completion record</i> written {fmt(status.last_sync.finished_at)} · syncs since then may still be running or hung mid-way (data above is the truth).
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-3">
              <Button onClick={syncNow} disabled={syncing} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="drchrono-sync-btn">
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <RefreshCcw className="h-4 w-4 mr-2"/>}
                {syncing ? (syncPhase || "Syncing…") : "Sync now"}
              </Button>
              <Button onClick={disconnect} variant="outline" className="border-[#E7C9C1] text-[#8A3E30]" data-testid="drchrono-disconnect-btn">
                <Unplug className="h-4 w-4 mr-2"/> Disconnect
              </Button>
            </div>
            {clientIdPrefix && (
              <p className="text-xs text-[#8A847A] pt-2">App: <span className="font-mono">{clientIdPrefix}...</span></p>
            )}
            {status?.backend_build && (
              <div className="mt-2 p-2 rounded bg-[#EEF2ED] border border-[#D5DED3] text-[10px] font-mono text-[#3B5C46]" data-testid="backend-build-marker">
                <div><b>Backend build:</b> {status.backend_build}</div>
                <div><b>Features live:</b> clinical_notes date_range fix {status.code_features?.clinical_notes_date_range_fix ? "✓" : "✗"} · auto-relink {status.code_features?.auto_relink_orphans ? "✓" : "✗"} · auto-cancel-stale {status.code_features?.auto_cancel_stale_jobs ? "✓" : "✗"} · {status.code_features?.max_sync_minutes || "?"}-min budget</div>
              </div>
            )}
            <details className="mt-3 border-t border-[#E5E0D8] pt-3">
              <summary className="text-xs uppercase tracking-[0.08em] text-[#8A847A] cursor-pointer select-none hover:text-[#5C5850]" data-testid="advanced-toggle">
                Advanced diagnostics
              </summary>
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => syncNow(true)} disabled={syncing} variant="outline" size="sm" className="border-[#4A6B5D] text-[#4A6B5D]" data-testid="drchrono-force-sync-btn">
                    <RefreshCcw className="h-4 w-4 mr-2"/> Force sync (bypass 12h cooldown)
                  </Button>
                  <Button onClick={runProbe} disabled={probing} variant="outline" size="sm" data-testid="drchrono-probe-btn">
                    {probing ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Activity className="h-4 w-4 mr-2"/>}
                    Quick probe
                  </Button>
                  <Button onClick={cancelStuck} disabled={cancelling} variant="outline" size="sm" className="border-[#E7C9C1] text-[#8A3E30]" data-testid="drchrono-cancel-stuck-btn">
                    {cancelling ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <XCircle className="h-4 w-4 mr-2"/>}
                    Cancel stuck sync
                  </Button>
                  <Button onClick={runDiagnose} disabled={diagnosing} variant="outline" size="sm" data-testid="drchrono-diagnose-btn">
                    {diagnosing ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Search className="h-4 w-4 mr-2"/>}
                    Diagnose visits
                  </Button>
                  <Button onClick={relinkOrphans} disabled={relinking} variant="outline" size="sm" className="border-[#4A6B5D] text-[#4A6B5D]" data-testid="drchrono-relink-btn">
                    {relinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Wrench className="h-4 w-4 mr-2"/>}
                    Re-link orphan visits
                  </Button>
                  <Button onClick={loadHistory} disabled={loadingHistory} variant="outline" size="sm" data-testid="drchrono-history-btn">
                    {loadingHistory ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Activity className="h-4 w-4 mr-2"/>}
                    Full sync history
                  </Button>
                  <Button onClick={runReconcile} disabled={reconciling} variant="outline" size="sm" className="border-[#4A6B5D] text-[#4A6B5D]" data-testid="drchrono-reconcile-btn">
                    {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Activity className="h-4 w-4 mr-2"/>}
                    Verify vs DrChrono
                  </Button>
                  <Button onClick={backfillCpt} disabled={backfillingCpt} variant="outline" size="sm" className="border-[#4A6B5D] text-[#4A6B5D]" data-testid="drchrono-backfill-cpt-btn">
                    {backfillingCpt ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Wrench className="h-4 w-4 mr-2"/>}
                    Backfill CPT codes
                  </Button>
                  <Button onClick={hardReset} variant="outline" size="sm" className="border-[#E5D9BE] text-[#8A6E2A]" data-testid="drchrono-hard-reset-btn">
                    <AlertTriangle className="h-4 w-4 mr-2"/> Hard reset
                  </Button>
                </div>
                {probeResult && (
                  <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8] text-xs font-mono">
                    <div className="font-semibold mb-1">Probe result:</div>
                    <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(probeResult, null, 2)}</pre>
                  </div>
                )}
                {diagResult && (
                  <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8] text-xs" data-testid="drchrono-diagnose-result">
                    <div className="font-semibold mb-2">Visit diagnostics — copy/paste this to me:</div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">Total visits</div><div className="font-mono font-bold text-lg text-[#292723]">{diagResult.totals?.visits_total?.toLocaleString()}</div></div>
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">Linked to NP</div><div className="font-mono font-bold text-lg text-[#3B5C46]">{diagResult.totals?.with_np?.toLocaleString()}</div></div>
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A3E30]">Orphan (np_id null)</div><div className="font-mono font-bold text-lg text-[#8A3E30]">{((diagResult.totals?.orphan_np_null || 0) + (diagResult.totals?.orphan_np_missing || 0)).toLocaleString()}</div></div>
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">With CPT codes</div><div className="font-mono font-bold text-lg text-[#292723]">{diagResult.totals?.with_cpt?.toLocaleString()}</div></div>
                    </div>
                    <pre className="text-[10px] whitespace-pre-wrap max-h-96 overflow-auto bg-white p-2 rounded border border-[#E5E0D8]">{JSON.stringify(diagResult, null, 2)}</pre>
                  </div>
                )}
                {reconcileResult && (
                  <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8] text-xs" data-testid="drchrono-reconcile-result">
                    <div className="font-semibold mb-2">DrChrono vs Emergent — {reconcileResult.window?.from} → {reconcileResult.window?.to}</div>
                    <div className="overflow-auto border border-[#E5E0D8] rounded bg-white">
                      <table className="w-full text-[11px]">
                        <thead className="bg-[#F7F5F0]">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Entity</th>
                            <th className="px-2 py-1.5 text-right">DrChrono</th>
                            <th className="px-2 py-1.5 text-right">Emergent</th>
                            <th className="px-2 py-1.5 text-right">Delta</th>
                            <th className="px-2 py-1.5 text-center">Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconcileResult.rows?.map(r => (
                            <tr key={r.entity} className="border-t border-[#E5E0D8]">
                              <td className="px-2 py-1.5 font-mono">{r.entity}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{r.error ? "err" : r.drchrono?.toLocaleString() ?? "—"}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{r.emergent?.toLocaleString()}</td>
                              <td className={`px-2 py-1.5 text-right font-mono ${r.delta === 0 ? "text-[#3B5C46]" : r.delta < 0 ? "text-[#8A3E30]" : "text-[#B08A55]"}`}>
                                {r.delta === null ? "—" : (r.delta > 0 ? "+" : "") + r.delta.toLocaleString()}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {r.error ? <span className="text-[#8A3E30]">⚠</span> : r.match ? <span className="text-[#3B5C46]">✓</span> : <span className="text-[#B08A55]">⚠</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-[10px] text-[#8A847A]">
                      ✓ = perfect match · ⚠ = gap (may be expected — e.g. patients includes archived) · Negative delta = you're MISSING data.
                      {reconcileResult.rows?.some(r => r.error?.includes("429")) && <span className="text-[#8A3E30] block mt-1">DrChrono rate-limited on some rows — retry in an hour for accurate numbers.</span>}
                    </div>
                    <details className="mt-2">
                      <summary className="text-[10px] uppercase text-[#8A847A] cursor-pointer">Raw JSON</summary>
                      <pre className="text-[10px] whitespace-pre-wrap max-h-64 overflow-auto bg-white p-2 rounded border border-[#E5E0D8] mt-1">{JSON.stringify(reconcileResult, null, 2)}</pre>
                    </details>
                  </div>
                )}
                {historyResult && (
                  <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8] text-xs" data-testid="drchrono-history-result">
                    <div className="font-semibold mb-2">Full sync history — paste this to me:</div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">Completed syncs</div><div className="font-mono font-bold text-lg text-[#292723]">{historyResult.summary?.syncs_recorded}</div></div>
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">Sync jobs (any state)</div><div className="font-mono font-bold text-lg text-[#292723]">{historyResult.summary?.jobs_recorded}</div></div>
                      <div className="p-2 bg-white rounded border border-[#E5E0D8]"><div className="text-[10px] uppercase text-[#8A847A]">Job phases</div><div className="text-[10px] font-mono text-[#292723]">{Object.entries(historyResult.summary?.jobs_by_phase || {}).map(([k,v]) => `${k}:${v}`).join(' · ')}</div></div>
                    </div>
                    <pre className="text-[10px] whitespace-pre-wrap max-h-96 overflow-auto bg-white p-2 rounded border border-[#E5E0D8]">{JSON.stringify(historyResult, null, 2)}</pre>
                  </div>
                )}
                {status.last_sync?.endpoint_errors && Object.keys(status.last_sync.endpoint_errors).length > 0 && (
                  <div className="p-2 rounded bg-[#FBEBE6] border border-[#E7C9C1] text-[10px] font-mono text-[#8A3E30]">
                    <div className="font-semibold mb-1">Endpoint errors during last sync:</div>
                    {Object.entries(status.last_sync.endpoint_errors).map(([k, v]) => (
                      <div key={k}><b>{k}</b>: {v}</div>
                    ))}
                  </div>
                )}
              </div>
            </details>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <Button onClick={connect} disabled={connecting} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="drchrono-connect-btn">
              {connecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <LinkIcon className="h-4 w-4 mr-2"/>}
              Connect DrChrono
            </Button>
            <p className="text-xs text-[#8A847A]">
              Requests scopes: user:read, calendar:read, clinical:read, billing:read, patients:summary:read.
              Log into DrChrono as a Doctor account (with share_patients on) before clicking Connect for practice-wide access.
            </p>
            {clientIdPrefix && <p className="text-xs text-[#8A847A]">App configured: <span className="font-mono">{clientIdPrefix}...</span></p>}
            {!clientIdPrefix && <p className="text-xs text-[#8A3E30]">⚠ No DRCHRONO_CLIENT_ID in env — configure before connecting.</p>}
          </div>
        )}

        {callbackError && !status?.connected && (
          <div className="mt-6 p-3 rounded bg-[#FBEBE6] border border-[#E7C9C1] text-xs">
            <div className="font-semibold text-[#8A3E30] mb-1">Last authorization failure:</div>
            <pre className="text-[10px] font-mono whitespace-pre-wrap text-[#8A3E30]">{callbackError.err || callbackError.reason}</pre>
            {callbackError.redirect_uri_sent && (
              <div className="mt-2 text-[#5C5850]">Redirect URI sent: <code>{callbackError.redirect_uri_sent}</code></div>
            )}
          </div>
        )}
      </div>

      {status?.connected && <DoctorMappingsPanel />}
    </div>
  );
}

function DoctorMappingsPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("unmapped");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/drchrono/doctor-map");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load doctor mappings"); }
    finally { setLoading(false); }
  };

  const assign = async (did, npId) => {
    setSavingId(did);
    try {
      if (!npId) {
        const r = await api.post("/drchrono/unmap-doctor", { drchrono_doctor_id: did });
        toast.success(`Cleared · ${r.data.visits_cleared} visits unlinked`);
      } else {
        const r = await api.post("/drchrono/map-doctor", { drchrono_doctor_id: did, np_id: npId });
        toast.success(`${r.data.np} · ${r.data.visits_backfilled} visits linked`, { duration: 8000 });
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingId(null); }
  };

  const convertToNp = async (did, name) => {
    if (!window.confirm(`Convert "${name}" from MD to NP and link all their visits?`)) return;
    setSavingId(did);
    try {
      const r = await api.post("/drchrono/convert-md-to-np", { drchrono_doctor_id: did });
      toast.success(`${r.data.np_name} is now an NP · ${r.data.visits_backfilled} visits linked`, { duration: 10000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
    finally { setSavingId(null); }
  };

  const rows = data?.doctors || [];
  const filtered = filter === "all" ? rows : rows.filter(r => !r.np_id);
  const unmappedCount = rows.filter(r => !r.np_id).length;
  const unmappedVisits = rows.filter(r => !r.np_id).reduce((a, r) => a + (r.visit_count || 0), 0);

  return (
    <div className="mt-6 bg-white border border-[#E5E0D8] rounded-md p-6" data-testid="drchrono-doctor-map-panel">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-[#F0EBE1] grid place-items-center">
          <Users className="h-5 w-5 text-[#4A6B5D]"/>
        </div>
        <div className="flex-1">
          <h2 className="heading text-xl font-semibold text-[#292723]">Doctor → NP mappings</h2>
          <p className="text-sm text-[#5C5850] mt-0.5">Link each DrChrono doctor to an NP so their visits show up on the NP's page.</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm" data-testid="load-doctor-map-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCcw className="h-4 w-4"/>}
          <span className="ml-1">{data ? "Reload" : "Load mappings"}</span>
        </Button>
      </div>

      {data && (
        <>
          {data.source && data.source !== "drchrono" && (
            <div className="mt-3 p-2 rounded bg-[#FEF7E6] border border-[#E5D9BE] text-[10px] text-[#8A6E2A]">
              ⚠ DrChrono is throttled — showing names from {data.source === "cache" ? "last-sync cache" : "local visit data (names unavailable)"}. Refresh once quota clears for latest.
            </div>
          )}
          <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
            <div className="bg-[#F7F5F0] border border-[#E5E0D8] rounded p-3">
              <div className="uppercase tracking-[0.08em] text-[#5C5850]">DrChrono doctors</div>
              <div className="text-2xl font-bold text-[#292723] mt-1">{rows.length}</div>
            </div>
            <div className="bg-[#F7F5F0] border border-[#E5E0D8] rounded p-3">
              <div className="uppercase tracking-[0.08em] text-[#8A3E30]">Unmapped</div>
              <div className="text-2xl font-bold text-[#8A3E30] mt-1">{unmappedCount}</div>
            </div>
            <div className="bg-[#F7F5F0] border border-[#E5E0D8] rounded p-3">
              <div className="uppercase tracking-[0.08em] text-[#8A3E30]">Orphan visits</div>
              <div className="text-2xl font-bold text-[#8A3E30] mt-1">{unmappedVisits.toLocaleString()}</div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={() => setFilter("unmapped")}
              className={`text-xs px-3 py-1.5 rounded border ${filter === "unmapped" ? "bg-[#4A6B5D] text-white border-[#4A6B5D]" : "bg-white border-[#E5E0D8] text-[#5C5850]"}`}
              data-testid="filter-unmapped-btn">Unmapped ({unmappedCount})</button>
            <button onClick={() => setFilter("all")}
              className={`text-xs px-3 py-1.5 rounded border ${filter === "all" ? "bg-[#4A6B5D] text-white border-[#4A6B5D]" : "bg-white border-[#E5E0D8] text-[#5C5850]"}`}
              data-testid="filter-all-btn">All ({rows.length})</button>
          </div>

          <div className="mt-4 overflow-auto max-h-[500px] border border-[#E5E0D8] rounded">
            <table className="w-full text-xs" data-testid="doctor-mappings-table">
              <thead className="bg-[#F7F5F0] sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">DrChrono name</th>
                  <th className="px-3 py-2 font-semibold">Visits</th>
                  <th className="px-3 py-2 font-semibold">Current mapping</th>
                  <th className="px-3 py-2 font-semibold">Assign to NP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.drchrono_doctor_id} className="border-t border-[#E5E0D8]">
                    <td className="px-3 py-2 font-mono">
                      <div className="text-[#292723]">{r.drchrono_name || "—"}</div>
                      {r.email && <div className="text-[10px] text-[#8A847A]">{r.email}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold">{r.visit_count}</td>
                    <td className="px-3 py-2">
                      {r.np_name ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-[#E8EDE5] text-[#3B5C46] text-[10px]">NP: {r.np_name}</span>
                      ) : r.md_name ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-[#F0EBE1] text-[#8A6E2A] text-[10px]">MD: {r.md_name}</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded bg-[#FBEBE6] text-[#8A3E30] text-[10px]">Unmapped</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={r.np_id || ""}
                          onChange={(e) => assign(r.drchrono_doctor_id, e.target.value)}
                          disabled={savingId === r.drchrono_doctor_id}
                          className="text-xs border border-[#E5E0D8] rounded px-2 py-1 bg-white"
                          data-testid={`assign-np-${r.drchrono_doctor_id}`}
                        >
                          <option value="">— select NP —</option>
                          {(data.nps || []).map(n => (
                            <option key={n.id} value={n.id}>{n.name}</option>
                          ))}
                        </select>
                        {r.md_name && !r.np_id && (
                          <button
                            onClick={() => convertToNp(r.drchrono_doctor_id, r.md_name)}
                            disabled={savingId === r.drchrono_doctor_id}
                            className="text-[10px] px-2 py-1 rounded bg-[#4A6B5D] text-white hover:bg-[#3E594D] disabled:opacity-50"
                            data-testid={`convert-to-np-${r.drchrono_doctor_id}`}
                          >Convert to NP</button>
                        )}
                        {savingId === r.drchrono_doctor_id && <Loader2 className="h-3 w-3 inline animate-spin"/>}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-[#8A847A]">No doctors in this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
